<script setup>
import { computed, inject, useAttrs } from "vue";
import { useVModel } from "@vueuse/core";
import { nanoid } from "nanoid";
import BaseErrorList from "~/components/base/BaseErrorList";
import { cn } from "~/utils/cn";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  label: { type: String, default: null },
  modelValue: { type: Array, default: () => [] },
  options: {
    type: Array,
    required: true,
    validator: (options) => options.every((option) => "value" in option && "label" in option),
  },
  hideLabel: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  errors: { type: [Array, String], default: () => [] },
});

// Can include form, table, modal
const renderContext = inject("renderContext", []);
const emits = defineEmits(["update:modelValue"]);

const { class: wrapperClass, ...attrs } = useAttrs();
const modelValue = useVModel(props, "modelValue", emits, {
  passive: true,
});
const name = computed(() => attrs.name || attrs.id || nanoid(8));
const disabled = computed(() => (attrs.disabled || attrs.disabled === "" ? true : undefined));
const groupId = computed(() => attrs.id || name.value);
const optionId = (value) => `${groupId.value}-${value}`;
</script>

<template>
  <fieldset :id="groupId" :class="cn(renderContext.includes('form') && 'my-4', wrapperClass)">
    <legend v-if="label && !hideLabel" class="block text-sm mb-1 font-medium leading-6 text-gray-700">
      {{ label }}
      <span v-if="required" class="text-red-600">*</span>
    </legend>

    <div class="border border-gray-300 bg-white shadow-xs rounded-md text-sm divide-y">
      <label v-for="option in options" :key="option.value" :for="optionId(option.value)" class="block cursor-pointer">
        <div class="p-4">
          <div class="flex gap-2 items-center">
            <input
              v-bind="attrs"
              :id="optionId(option.value)"
              v-model="modelValue"
              type="checkbox"
              :name="name"
              :value="option.value"
              :disabled="disabled"
              class="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 disabled:border-gray-200 disabled:bg-gray-100"
            />
            <p class="font-medium leading-none text-gray-900">{{ option.label }}</p>
          </div>
          <p v-if="option.description" class="ml-6 mt-1 text-gray-700">{{ option.description }}</p>
        </div>
      </label>
    </div>

    <BaseErrorList :errors="errors" />
  </fieldset>
</template>
